/**
 * Onboarding Flow Hook
 *
 * Main orchestration hook that composes smaller, focused hooks.
 * Manages the overall onboarding flow state and coordinates actions.
 *
 * Supports different onboarding flow types via the optional `startApi` parameter.
 */

import { useState, useCallback } from 'react';
import * as api from '@/features/onboarding/api/onboarding-api';
import {
  defaultOnboardingApiClient,
  type OnboardingApiClient,
} from '@/features/onboarding/api/onboarding-api-client';
import { SMS_RESEND_COUNTDOWN_SECONDS } from '@/features/onboarding/constants/onboarding.constants';
import { initialOnboardingState } from '@/features/onboarding/hooks/onboarding-state';
import { useFactorActions } from '@/features/onboarding/hooks/use-factor-actions';
import { useJwtExchange } from '@/features/onboarding/hooks/use-jwt-exchange';
import { useOnboardingResponseHandlers } from '@/features/onboarding/hooks/use-onboarding-response-handlers';
import { useOtpActions } from '@/features/onboarding/hooks/use-otp-actions';
import {
  FlowStatus,
  FactorType,
  type OnboardingState,
  type OnboardingFlowResponse,
} from '@/features/onboarding/types/onboarding.types';

interface UseOnboardingFlowOptions {
  /** Magic-link token (required to start flow) */
  token: string;
  /**
   * Custom start API function - allows different onboarding flows to use different endpoints
   * Defaults to standard user onboarding API
   */
  startApi?: (token: string) => Promise<OnboardingFlowResponse>;
  /**
   * Injectable API client used by the sub-hooks (submit/choose/complete/…).
   * Defaults to the standard user-onboarding endpoints. The
   * authenticator-enrollment-invitation flow passes its own BFF-backed client.
   */
  api?: OnboardingApiClient;
  /**
   * Override the post-submit auto-complete decision. See ``useFactorActions``
   * for the contract — wizards with a post-completion UX detour (device
   * pairing) return false to keep the onboarding session cookie alive until
   * the detour finishes.
   */
  shouldAutoComplete?: (response: OnboardingFlowResponse) => boolean;
}

/**
 * Hook for managing the onboarding flow
 *
 * @param options - Configuration options including token and optional custom startApi
 */
export function useOnboardingFlow(options: UseOnboardingFlowOptions | string) {
  // Support both old signature (string) and new signature (options object)
  const {
    token,
    startApi = api.startOnboardingFlow,
    api: apiClient = defaultOnboardingApiClient,
    shouldAutoComplete,
  } = typeof options === 'string'
    ? { token: options, shouldAutoComplete: undefined }
    : options;
  const [state, setState] = useState<OnboardingState>(initialOnboardingState);

  const { callCompleteApi, processResponse } = useOnboardingResponseHandlers({
    apiClient,
    exchangeToken: state.exchangeToken,
    setState,
  });

  // ============================================================================
  // COMPOSE SMALLER HOOKS
  // ============================================================================

  const otpActions = useOtpActions({
    state,
    setState,
    processResponse,
    api: apiClient,
  });
  const factorActions = useFactorActions({
    setState,
    processResponse,
    callCompleteApi,
    api: apiClient,
    shouldAutoComplete,
  });
  const jwtExchange = useJwtExchange({ state, setState });

  // ============================================================================
  // FLOW CONTROL ACTIONS
  // ============================================================================

  /**
   * Handle "Get Started" button click
   * Calls the configured start API and transitions to the step returned by backend
   */
  const handleGetStarted = useCallback(async () => {
    setState(prev => ({ ...prev, isLoading: true, error: null }));

    try {
      const response = await startApi(token);

      // Start OTP countdown if first step is SMS or WhatsApp OTP
      const isOtpStep =
        response.currentStep?.factor_type === FactorType.SMS_OTP ||
        response.currentStep?.factor_type === FactorType.WHATSAPP_OTP;

      await processResponse(response);

      if (isOtpStep) {
        setState(prev => ({
          ...prev,
          smsResendCountdown: SMS_RESEND_COUNTDOWN_SECONDS,
        }));
      }
    } catch (error) {
      setState(prev => ({
        ...prev,
        hasStarted: true,
        flowStatus: FlowStatus.FAILED,
        error: error instanceof Error ? error.message : 'Failed to start',
        isLoading: false,
      }));
    }
  }, [token, startApi, processResponse]);

  /**
   * Final completion - called from "Go to My Apps" button in the success summary
   * Calls /complete API if exchange token is not available, then transitions to COMPLETED.
   */
  const handleFinalComplete = useCallback(async () => {
    await callCompleteApi();
  }, [callCompleteApi]);

  /**
   * Legacy completion handler - kept for backwards compatibility
   * Parent component handles redirect
   */
  const handleComplete = useCallback(async () => {
    setState(prev => ({ ...prev, isLoading: true }));
    // No API call needed - backend already marked flow as complete
    // JWT exchange should have already happened via exchangeForSession
    setState(prev => ({ ...prev, isLoading: false }));
  }, []);

  // ============================================================================
  // UTILITIES
  // ============================================================================

  /**
   * Clear error
   */
  const clearError = useCallback(() => {
    setState(prev => ({ ...prev, error: null }));
  }, []);

  // ============================================================================
  // COMPUTED VALUES
  // ============================================================================

  const isComplete = state.flowStatus === FlowStatus.COMPLETED;

  // ============================================================================
  // RETURN COMBINED API
  // ============================================================================

  return {
    state,

    // Flow control
    handleGetStarted,
    handleComplete,
    handleFinalComplete,
    clearError,

    // OTP actions (from useOtpActions)
    submitSmsCode: otpActions.submitSmsCode,
    resendSmsCode: otpActions.resendSmsCode,
    canResendSms: otpActions.canResendSms,

    // Factor actions (from useFactorActions)
    submitPrimaryFactor: factorActions.submitPrimaryFactor,
    completeSecondaryFactors: factorActions.completeSecondaryFactors,
    submitFactor: factorActions.submitFactor,
    chooseAlternative: factorActions.chooseAlternative,
    goBackToAlternatives: factorActions.goBackToAlternatives,

    // JWT Exchange (from useJwtExchange)
    exchangeForSession: jwtExchange.exchangeForSession,
    canExchange: jwtExchange.canExchange,

    // Computed
    isComplete,
  };
}
